import React, { useState, useEffect, useContext } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import SalaryTable from "./Salary/SalaryTable";
import SalaryDetail from "./Salary/SalaryDetail/SalaryDetail";
import { getSalaryApprovalsByApproverV2 } from "../../api/timesheetApi";
import "./FinancialReport.css";
import { UserContext } from "../../contexts/CurrentUserContext";

registerLocale("ru", ru);
setDefaultLocale("ru");

function Salary() {
  const { userData } = useContext(UserContext);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizeStatus = (status) => {
    switch ((status || "").toLowerCase()) {
      case "approved":
      case "согласован":
        return "Согласован";
      case "rejected":
      case "declined":
      case "отклонено":
      case "не согласован":
        return "Отклонено";
      case "pending":
      case "на рассмотрении":
      default:
        return "На рассмотрении";
    }
  };

  const formatPersonShortName = (person) => {
    if (!person) return "-";
    const surname = person.surname || "";
    const firstInitial = person.name?.[0] ? `${person.name[0]}.` : "";
    return `${surname} ${firstInitial}`.trim() || "-";
  };

  // Загрузка списка согласований зарплат
  useEffect(() => {
    const loadDepartments = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (!userData?.id) {
          setDepartments([]);
          return;
        }

        const selectedYear = selectedMonth.getFullYear();
        const selectedMonthNumber = selectedMonth.getMonth() + 1;
        const approvals = await getSalaryApprovalsByApproverV2(userData.id);
        const filteredApprovals = approvals.filter(
          (approval) =>
            Number(approval?.year) === selectedYear &&
            Number(approval?.month) === selectedMonthNumber,
        );

        const mappedDepartments = filteredApprovals.map((approval) => ({
          id: approval.department?.id ?? approval.id,
          approvalId: approval.id,
          name: approval.department?.name || "Неизвестный отдел",
          initiator: formatPersonShortName(approval.initiator),
          approvers: (approval.steps || []).map((step, index) => ({
            role: `Согласующий ${index + 1}`,
            name: formatPersonShortName(step.approver),
            status: normalizeStatus(step.status),
            comment: step.comment || "",
            decidedAt: step.decided_at ? new Date(step.decided_at) : null,
          })),
          submissionDate: approval.created_at
            ? new Date(approval.created_at)
            : null,
          approvedAt: approval.approved_at
            ? new Date(approval.approved_at)
            : null,
          status: normalizeStatus(approval.status),
          year: approval.year,
          month: approval.month,
        }));

        setDepartments(mappedDepartments);
      } catch (err) {
        console.error("Ошибка при загрузке отделов:", err);
        setError(err.message || "Ошибка при загрузке данных");
        setDepartments([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadDepartments();
  }, [selectedMonth, userData?.id]);

  const handleDepartmentClick = (department) => {
    setIsCreateMode(false);
    setSelectedDepartment(department);
  };

  const handleCreateApprovalClick = () => {
    setSelectedDepartment(null);
    setIsCreateMode(true);
  };

  const handleBack = () => {
    setIsCreateMode(false);
    setSelectedDepartment(null);
  };

  // Если выбран отдел или режим создания согласования, показываем детальную страницу
  if (selectedDepartment || isCreateMode) {
    return (
      <SalaryDetail
        department={isCreateMode ? null : selectedDepartment}
        onBack={handleBack}
        initialMonth={selectedMonth}
      />
    );
  }

  // Иначе показываем список отделов
  return (
    <div className="financial-report">
      <div style={{ padding: "20px" }}>
        <h2>Заработная плата</h2>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          <label>Месяц:</label>
          <div style={{ width: "200px" }}>
            <DatePicker
              selected={selectedMonth}
              onChange={(date) => setSelectedMonth(date)}
              dateFormat="MM.yyyy"
              showMonthYearPicker
              locale="ru"
              className="timesheet-datepicker"
              calendarClassName="custom-calendar month-year-calendar"
            />
          </div>
          <button
            type="button"
            onClick={handleCreateApprovalClick}
            style={{
              height: "40px",
              padding: "0 16px",
              borderRadius: "8px",
              // border: "1px solid #d1d5db",
              background: "#2A5E38",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "14px",
              fontFamily: "Manrope",
            }}
          >
            Создать согласование
          </button>
        </div>
        {isLoading && (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <p>Загрузка данных...</p>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: "20px", color: "red", textAlign: "center" }}>
            <p>Ошибка: {error}</p>
          </div>
        )}
        {!isLoading && (
          <SalaryTable
            selectedMonth={selectedMonth}
            departments={departments}
            onDepartmentClick={handleDepartmentClick}
          />
        )}
      </div>
    </div>
  );
}

export default Salary;
